import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { faRedo, faTrashCan, faSearch } from '@fortawesome/free-solid-svg-icons';
import Pagination from '../../components/Pagination';
import ConfirmationModal from '../../components/ConfirmationModal';
import TextInput from '../../components/TextInput';
import Button from '../../components/Button';
import SkeletonLoader from '../../components/SkeletonLoader';
import workEducationApi from '../../api/workEducationApi';
import { useToast } from '../../contexts/ToastContext';

const TrashedWorkEducation = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { addToast } = useToast();
  const [workEducation, setWorkEducation] = useState([]);
  const [loading, setLoading] = useState(true);
  const [itemsPerPage] = useState(10);

  const [isRestoreModalOpen, setIsRestoreModalOpen] = useState(false);
  const [isPermanentDeleteModalOpen, setIsPermanentDeleteModalOpen] = useState(false);
  const [itemToActOnId, setItemToActOnId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [error, setError] = useState(null);

  const [lastPage, setLastPage] = useState(1);

  const queryParams = new URLSearchParams(location.search);
  const currentPage = parseInt(queryParams.get('page')) || 1;

  useEffect(() => {
    const timerId = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);

    return () => {
      clearTimeout(timerId);
    };
  }, [searchTerm]);

  const fetchTrashedWorkEducation = useCallback(async (page, perPage, search) => {
    setLoading(true);
    setError(null);
    try {
      const response = await workEducationApi.getWorkEducation({ page, perPage, search, includeTrashed: true });
      setWorkEducation(response.data);
      setLastPage(response.pagination.last_page);
    } catch (err) {
      console.error('Failed to fetch trashed work and education entries:', err);
      setError('Failed to load trashed work and education entries. Please try again later.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTrashedWorkEducation(currentPage, itemsPerPage, debouncedSearchTerm);
  }, [fetchTrashedWorkEducation, currentPage, itemsPerPage, debouncedSearchTerm]);

  const handleRestoreClick = (id) => {
    setItemToActOnId(id);
    setIsRestoreModalOpen(true);
  };

  const handleConfirmRestore = async () => {
    setIsRestoreModalOpen(false);
    if (itemToActOnId) {
      setLoading(true);
      setError(null);
      try {
        await workEducationApi.restoreWorkEducation(itemToActOnId);
        addToast('Work/Education entry restored successfully!', 'success');
        await fetchTrashedWorkEducation(currentPage, itemsPerPage, debouncedSearchTerm);
      } catch (err) {
        console.error('Failed to restore work/education entry:', err);
        addToast(err.response?.data?.message || 'Failed to restore work/education entry. Please try again.', 'error');
        setError('Failed to restore work/education entry. Please try again.');
      } finally {
        setLoading(false);
        setItemToActOnId(null);
      }
    }
  };

  const handlePermanentDeleteClick = (id) => {
    setItemToActOnId(id);
    setIsPermanentDeleteModalOpen(true);
  };

  const handleConfirmPermanentDelete = async () => {
    setIsPermanentDeleteModalOpen(false);
    if (itemToActOnId) {
      setLoading(true);
      setError(null);
      try {
        await workEducationApi.permanentDeleteWorkEducation(itemToActOnId);
        addToast('Work/Education entry permanently deleted successfully!', 'success');
        await fetchTrashedWorkEducation(currentPage, itemsPerPage, debouncedSearchTerm);
      } catch (err) {
        console.error('Failed to permanently delete work/education entry:', err);
        addToast(err.response?.data?.message || 'Failed to permanently delete work/education entry. Please try again.', 'error');
        setError('Failed to permanently delete work/education entry. Please try again.');
      } finally {
        setLoading(false);
        setItemToActOnId(null);
      }
    }
  };

  const handleCancelAction = () => {
    setIsRestoreModalOpen(false);
    setIsPermanentDeleteModalOpen(false);
    setItemToActOnId(null);
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    const searchParams = new URLSearchParams(location.search);
    searchParams.set('page', 1);
    navigate(`${location.pathname}?${searchParams.toString()}`);
  };

  return (
    <div className="p-6 bg-gray-800 rounded-lg shadow-md text-text-white flex flex-col min-h-[calc(100vh-100px)]">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold text-text-white">Trashed Work & Education Entries</h1>
        <Button
          onClick={() => navigate('/work-education')}
          className="bg-accent-red hover:bg-red-700 text-white"
        >
          Back to Work & Education
        </Button>
      </div>

      <div className="mb-4">
        <TextInput
          label="Search Trashed Entries"
          id="search-trashed-entries"
          name="search-trashed-entries"
          value={searchTerm}
          onChange={handleSearchChange}
          placeholder="Search by designation, company or content..."
          icon={faSearch}
        />
      </div>

      {loading ? (
        <SkeletonLoader type="table" count={itemsPerPage} />
      ) : error ? (
        <div className="text-red-500 text-center py-4">{error}</div>
      ) : (
        <>
          <div className="overflow-x-auto flex-grow">
            <table className="min-w-full bg-gray-800 border border-gray-700">
              <thead>
                <tr>
                  <th className="py-3 px-4 border-b border-gray-700 text-left text-sm font-medium text-text-gray">ID</th>
                  <th className="py-3 px-4 border-b border-gray-700 text-left text-sm font-medium text-text-gray">Designation</th>
                  <th className="py-3 px-4 border-b border-gray-700 text-left text-sm font-medium text-text-gray">Company</th>
                  <th className="py-3 px-4 border-b border-gray-700 text-left text-sm font-medium text-text-gray">Duration</th>
                  <th className="py-3 px-4 border-b border-gray-700 text-left text-sm font-medium text-text-gray">Actions</th>
                </tr>
              </thead>
              <tbody>
                {workEducation.length > 0 ? (
                  workEducation.map((item) => (
                    <tr key={item.id} className="hover:bg-gray-700">
                      <td className="py-3 px-4 border-b border-gray-700 text-sm text-text-white">{item.id}</td>
                      <td className="py-3 px-4 border-b border-gray-700 text-sm text-text-white">{item.designation}</td>
                      <td className="py-3 px-4 border-b border-gray-700 text-sm text-text-white">{item.company_name}</td>
                      <td className="py-3 px-4 border-b border-gray-700 text-sm text-text-white">
                        {item.start_month} {item.start_year} - {item.end_year ? `${item.end_month} ${item.end_year}` : 'Present'}
                      </td>
                      <td className="py-3 px-4 border-b border-gray-700 text-sm flex items-center flex-nowrap">
                        <Button
                          onClick={() => handleRestoreClick(item.id)}
                          className="text-green-500 hover:text-green-600 mr-3"
                          icon={faRedo}
                          tooltipId={`restore-work-education-tooltip-${item.id}`}
                          tooltipContent="Restore"
                        />
                        <Button
                          onClick={() => handlePermanentDeleteClick(item.id)}
                          className="text-red-600 hover:text-red-700"
                          icon={faTrashCan}
                          tooltipId={`permanent-delete-work-education-tooltip-${item.id}`}
                          tooltipContent="Permanent Delete"
                        />
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="5" className="py-3 px-4 text-center text-sm text-text-gray">No trashed work/education entries found.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <Pagination
            currentPage={currentPage}
            totalPages={lastPage}
          />
        </>
      )}

      <ConfirmationModal
        isOpen={isRestoreModalOpen}
        onClose={handleCancelAction}
        onConfirm={handleConfirmRestore}
        title="Confirm Restore"
        message="Are you sure you want to restore this work/education entry?"
      />

      <ConfirmationModal
        isOpen={isPermanentDeleteModalOpen}
        onClose={handleCancelAction}
        onConfirm={handleConfirmPermanentDelete}
        title="Confirm Permanent Delete"
        message="Are you sure you want to permanently delete this work/education entry? This action cannot be undone."
      />
    </div>
  );
};

export default TrashedWorkEducation;